import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { invoiceNumberLabel, hasDisplayNumber } from '../utils/invoiceDisplay';
import toast from 'react-hot-toast';

interface Invoice {
  /** DOC ID — `{auctionId}_{uid}`. A key, not a label. Never shown as "your invoice number". */
  invoiceNumber: string;
  /** The human, sequential, buyer-facing number (8001…). Absent until backfilled. */
  displayInvoiceNumber?: number;
  auctionId: string;
  auctionTitle: string;
  bidderNumber: string;
  bidder: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  items: Array<{
    lotNumber: string;
    sku: string;
    title: string;
    quantity: number;
    highBid: number;
  }>;
  subtotal: number;
  buyersPremium: number;
  buyersPremiumRate: number;
  paymentType?: 'card' | 'cash'; // drives the BP rate on recompute; absent => 'card'
  salesTax: number;
  salesTaxRate: number;
  total: number;
  status: 'unpaid' | 'paid' | 'partial' | 'picked_up';
  pickStatus?: 'pending' | 'picked' | 'ready_for_pickup';
  storageLocation?: string;
  storagePhoto?: string;
  createdAt: string;
  paidAt?: string;
  paymentLink: string;
  editedAt?: string;
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

// Buyer's premium — resolved EXACTLY as process-native-closedown.mjs does:
//   1. the SALE's own auctions/{id}.buyersPremiumRate (a decimal; 0 = no premium), for every winner;
//   2. only if the sale sets none: the LEGACY constants by payment type, CARD 15% / CASH 12%
//      (absent / anything but 'cash' => CARD; cash is never inferred).
const BP_RATE_CARD = 0.15;
const BP_RATE_CASH = 0.12;
const bpRateForPaymentType = (pt?: string): number => (pt === 'cash' ? BP_RATE_CASH : BP_RATE_CARD);
/** saleRate: number = the sale's buyersPremiumRate; null = the sale sets none (legacy applies). */
const resolveBpRate = (saleRate: number | null, pt?: string): number =>
  typeof saleRate === 'number' ? saleRate : bpRateForPaymentType(pt);
const isValidRate = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1;

/**
 * Recompute money fields the SAME way process-native-closedown.mjs does, so an
 * edited invoice reprices IDENTICALLY to generation:
 *   subtotal = Σ highBid
 *   BP       = subtotal × bpRate   (bpRate from paymentType: card 0.15 / cash 0.12)
 *   tax      = subtotal × taxRate  ← HAMMERS ONLY; the buyer's premium is NOT taxed
 *   total    = subtotal + BP + tax
 * All values in dollars; rates are decimals.
 */
const recomputeTotals = (
  items: Array<{ highBid: number }>,
  paymentType: string | undefined,
  salesTaxRate: number,
  saleBpRate: number | null,
) => {
  const subtotal = round2(items.reduce((s, it) => s + (Number(it.highBid) || 0), 0));
  const buyersPremiumRate = resolveBpRate(saleBpRate, paymentType);
  const buyersPremium = round2(subtotal * buyersPremiumRate);
  const salesTax = round2(subtotal * salesTaxRate); // HAMMERS ONLY — premium untaxed
  const total = round2(subtotal + buyersPremium + salesTax);
  return { subtotal, buyersPremium, buyersPremiumRate, salesTax, total };
};

const InvoiceDetail: React.FC = () => {
  const { invoiceNumber } = useParams<{ invoiceNumber: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // F2 edit mode (auctioneer). Page is already SellerRoute, so any viewer here
  // is a seller/admin/consignor/auctioneer.
  const [editing, setEditing] = useState(false);
  const [editItems, setEditItems] = useState<Invoice['items']>([]);
  const [editTaxRatePct, setEditTaxRatePct] = useState('');
  const [saving, setSaving] = useState(false);
  // The sale's buyersPremiumRate: number = set, null = the sale sets none, undefined = not yet
  // read (or the read failed). Edits are NOT saved while undefined — falling back to the legacy
  // 15% because a read failed would re-charge a premium the sale may have waived.
  const [saleBpRate, setSaleBpRate] = useState<number | null | undefined>(undefined);

  useEffect(() => {
    loadInvoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceNumber]);

  const loadInvoice = async () => {
    if (!invoiceNumber) return;

    try {
      const docRef = doc(db, 'invoices', invoiceNumber);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const inv = docSnap.data() as Invoice;
        setInvoice(inv);
        // The sale decides the premium rate (see resolveBpRate). Read it once, fail closed.
        try {
          const saleSnap = inv.auctionId ? await getDoc(doc(db, 'auctions', inv.auctionId)) : null;
          const raw = saleSnap?.exists() ? (saleSnap.data() as any).buyersPremiumRate : undefined;
          if (raw === undefined || raw === null) setSaleBpRate(null);
          else if (isValidRate(raw)) setSaleBpRate(raw);
          else console.error(`auctions/${inv.auctionId}.buyersPremiumRate is not a decimal 0-1:`, raw);
        } catch (e) {
          console.error('Could not read the sale for its buyer\'s premium rate:', e);
        }
      } else {
        toast.error('Invoice not found');
        navigate('/');
      }
    } catch (error) {
      console.error('Error loading invoice:', error);
      toast.error('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !invoice) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be smaller than 5MB');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploading(true);
    try {
      // Upload to Firebase Storage
      const storageRef = ref(storage, `storage-photos/${invoice.invoiceNumber}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const photoURL = await getDownloadURL(storageRef);

      // Update invoice with photo URL
      await updateDoc(doc(db, 'invoices', invoice.invoiceNumber), {
        storagePhoto: photoURL,
        updatedAt: new Date().toISOString()
      });

      toast.success('Storage photo uploaded!');
      setInvoice({ ...invoice, storagePhoto: photoURL });
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleStatusUpdate = async (newStatus: 'unpaid' | 'paid') => {
    if (!invoice) return;

    try {
      await updateDoc(doc(db, 'invoices', invoice.invoiceNumber), {
        status: newStatus,
        ...(newStatus === 'paid' && { paidAt: new Date().toISOString() }),
        updatedAt: new Date().toISOString()
      });

      toast.success(`Invoice marked as ${newStatus}`);
      setInvoice({ ...invoice, status: newStatus, paidAt: newStatus === 'paid' ? new Date().toISOString() : invoice.paidAt });
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const startEdit = () => {
    if (!invoice) return;
    setEditItems(invoice.items.map((it) => ({ ...it })));
    setEditTaxRatePct(String(round2((invoice.salesTaxRate ?? 0) * 100)));
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditItems([]);
    setEditTaxRatePct('');
  };

  const removeEditItem = (index: number) => {
    setEditItems((prev) => prev.filter((_, i) => i !== index));
  };

  const changeEditPrice = (index: number, value: string) => {
    const price = parseFloat(value);
    setEditItems((prev) =>
      prev.map((it, i) => (i === index ? { ...it, highBid: isNaN(price) ? 0 : price } : it)),
    );
  };

  const editRate = (parseFloat(editTaxRatePct) || 0) / 100;
  const editPreview = invoice
    ? recomputeTotals(editItems, invoice.paymentType, editRate, saleBpRate ?? null)
    : { subtotal: 0, buyersPremium: 0, buyersPremiumRate: 0, salesTax: 0, total: 0 };

  const handleSaveEdit = async () => {
    if (!invoice) return;
    if (editItems.length === 0) {
      toast.error('An invoice must have at least one item. Remove the invoice instead if it has no lots.');
      return;
    }
    if (saleBpRate === undefined) {
      toast.error("Couldn't read this sale's buyer's premium rate, so the invoice can't be repriced safely. Reload and try again.");
      return;
    }
    setSaving(true);
    try {
      // recompute returns buyersPremiumRate (the sale's rate, else derived from paymentType), so
      // the stored rate stays consistent with the applied math on every edit.
      const totals = recomputeTotals(editItems, invoice.paymentType, editRate, saleBpRate);
      const patch = {
        items: editItems,
        salesTaxRate: editRate,
        paymentType: invoice.paymentType ?? 'card',
        ...totals,
        editedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await updateDoc(doc(db, 'invoices', invoice.invoiceNumber), patch);
      setInvoice({ ...invoice, ...patch });
      setEditing(false);
      toast.success('Invoice updated.');
    } catch (error) {
      console.error('Error saving invoice edit:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-center">
        <p className="text-gray-600">Loading invoice...</p>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-center">
        <p className="text-gray-600">Invoice not found</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header — screen only; these controls must not land on the printed invoice. */}
      <div className="flex justify-between items-start print:hidden">
        <button
          onClick={() => navigate(-1)}
          className="text-blue-600 hover:text-blue-800"
        >
          ← Back
        </button>
        <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
        >
          Print Invoice
        </button>
      </div>

      {/* Storage Location Section - PROMINENT */}
      {(invoice.storageLocation || invoice.storagePhoto || invoice.pickStatus === 'picked') && (
        <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6 print:hidden">
          {/* INTERNAL warehouse data (aisle/shelf note, storage photo, upload control).
              Never print it — this page is emailed to buyers. Staff who want a pick
              sheet use /picker/order/:invoiceNumber. */}
          <h2 className="text-xl font-bold text-yellow-900 mb-4 flex items-center gap-2">
            📍 Storage Location
          </h2>
          
          {invoice.storageLocation && (
            <div className="mb-4">
              <div className="text-sm text-yellow-700 font-semibold mb-1">Location Note:</div>
              <div className="text-2xl font-bold text-yellow-900">
                {invoice.storageLocation}
              </div>
            </div>
          )}

          {invoice.storagePhoto && (
            <div className="mb-4">
              <div className="text-sm text-yellow-700 font-semibold mb-2">Storage Photo:</div>
              <img
                src={invoice.storagePhoto}
                alt="Storage location"
                className="max-w-md rounded-lg shadow-lg cursor-pointer hover:shadow-xl transition"
                onClick={() => window.open(invoice.storagePhoto, '_blank')}
              />
              <p className="text-xs text-yellow-600 mt-1">Click to view full size</p>
            </div>
          )}

          {/* Upload Photo Option */}
          <div>
            <label className="block text-sm text-yellow-700 font-semibold mb-2">
              {invoice.storagePhoto ? 'Replace Photo:' : 'Add Storage Photo:'}
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              disabled={uploading}
              className="block w-full text-sm text-gray-900 border border-yellow-300 rounded-lg cursor-pointer bg-white focus:outline-none"
            />
            {uploading && (
              <p className="text-sm text-yellow-700 mt-2">Uploading...</p>
            )}
          </div>
        </div>
      )}

      {/* Invoice */}
      <div className="bg-white rounded-lg shadow-lg p-8 print:shadow-none">
        {/* Company Header */}
        <div className="text-center mb-8 border-b-2 border-gray-300 pb-6">
          <h1 className="text-3xl font-bold text-gray-900">Schmidt Auctions</h1>
          <p className="text-gray-600">Invoice</p>
        </div>

        {/* Invoice Info */}
        <div className="grid md:grid-cols-2 print:grid-cols-2 gap-6 mb-8">
          <div>
            <h2 className="text-lg font-bold mb-2">Bill To:</h2>
            <div className="text-gray-900">
              <p className="font-semibold">{invoice.bidder.firstName} {invoice.bidder.lastName}</p>
              <p className="text-sm">Bidder #{invoice.bidderNumber}</p>
              {invoice.bidder.address && <p className="text-sm">{invoice.bidder.address}</p>}
              {invoice.bidder.city && (
                <p className="text-sm">
                  {invoice.bidder.city}, {invoice.bidder.state} {invoice.bidder.zip}
                </p>
              )}
              <p className="text-sm mt-2">{invoice.bidder.email}</p>
              <p className="text-sm">{invoice.bidder.phone}</p>
            </div>
          </div>

          <div className="text-right">
            <div className="mb-4">
              <p className="text-sm text-gray-600">Invoice Number</p>
              {/* The human number leads — this page is what gets printed to PDF and
                  emailed to the buyer. The doc id stays as a small internal ref so
                  support can find the record when a buyer quotes 8001. */}
              <p className="text-2xl font-bold tracking-tight text-gray-900">
                {invoiceNumberLabel(invoice)}
              </p>
              {hasDisplayNumber(invoice) && (
                <p className="text-[10px] text-gray-400 font-mono break-all leading-tight mt-1">
                  ref {invoice.invoiceNumber}
                </p>
              )}
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-600">Auction</p>
              <p className="font-semibold">{invoice.auctionTitle}</p>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-600">Date</p>
              <p>{new Date(invoice.createdAt).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <span
                className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                  invoice.status === 'paid' || invoice.paidAt
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {invoice.status === 'paid' || invoice.paidAt ? 'Paid' : 'Unpaid'}
              </span>
              {/* Internal warehouse state — useful on screen, confusing on a
                  buyer's invoice ("Picked" means nothing to them). */}
              {invoice.pickStatus && (
                <span
                  className={`inline-block ml-2 px-3 py-1 rounded-full text-sm font-semibold print:hidden ${
                    invoice.pickStatus === 'ready_for_pickup'
                      ? 'bg-green-100 text-green-800'
                      : invoice.pickStatus === 'picked'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-orange-100 text-orange-800'
                  }`}
                >
                  {invoice.pickStatus === 'ready_for_pickup'
                    ? 'Ready'
                    : invoice.pickStatus === 'picked'
                    ? 'Picked'
                    : 'Pending'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="mb-8">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="text-left py-2">Lot</th>
                <th className="text-left py-2">SKU</th>
                <th className="text-left py-2">Description</th>
                <th className="text-center py-2">Qty</th>
                <th className="text-right py-2">Price</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, index) => (
                <tr key={index} className="border-b border-gray-200">
                  <td className="py-3">{item.lotNumber}</td>
                  <td className="py-3 text-sm text-gray-600">{item.sku}</td>
                  <td className="py-3">{item.title}</td>
                  <td className="py-3 text-center">{item.quantity}</td>
                  <td className="py-3 text-right">${(item.highBid ?? 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="flex justify-end print:break-inside-avoid">
          <div className="w-64">
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Subtotal:</span>
              <span className="font-semibold">${(invoice.subtotal ?? 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600">
                Buyer's Premium ({((invoice.buyersPremiumRate ?? 0) * 100).toFixed(0)}%):
              </span>
              <span className="font-semibold">${(invoice.buyersPremium ?? 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-300">
              <span className="text-gray-600">
                Sales Tax ({((invoice.salesTaxRate ?? 0) * 100).toFixed(1)}%):
              </span>
              <span className="font-semibold">${(invoice.salesTax ?? 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-3 border-b-2 border-gray-900">
              <span className="text-lg font-bold">Total:</span>
              <span className="text-lg font-bold text-green-600">
                ${(invoice.total ?? 0).toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Payment Link */}
        {invoice.paymentLink && invoice.status === 'unpaid' && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg print:break-inside-avoid">
            <p className="text-sm text-gray-700 mb-2">Pay online:</p>
            <a
              href={invoice.paymentLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 text-sm break-all"
            >
              {invoice.paymentLink}
            </a>
          </div>
        )}
      </div>

      {/* F2 — Edit panel (auctioneer). Recomputes BP + tax + total on save. */}
      {editing && (
        <div className="bg-white rounded-lg shadow p-6 print:hidden border-2 border-blue-300">
          <h3 className="font-bold mb-4">Edit Invoice</h3>
          <table className="w-full mb-4">
            <thead>
              <tr className="border-b-2 border-gray-300 text-sm text-gray-500">
                <th className="text-left py-2">Lot</th>
                <th className="text-left py-2">Description</th>
                <th className="text-right py-2">Price ($)</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {editItems.map((item, index) => (
                <tr key={index} className="border-b border-gray-200">
                  <td className="py-2">{item.lotNumber}</td>
                  <td className="py-2">{item.title}</td>
                  <td className="py-2 text-right">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.highBid}
                      onChange={(e) => changeEditPrice(index, e.target.value)}
                      className="w-28 px-2 py-1 border rounded text-sm text-right"
                    />
                  </td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      onClick={() => removeEditItem(index)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex items-center gap-3 mb-4">
            <label className="text-sm text-gray-700">Sales tax rate (%):</label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={editTaxRatePct}
              onChange={(e) => setEditTaxRatePct(e.target.value)}
              className="w-24 px-2 py-1 border rounded text-sm"
            />
            <button
              type="button"
              onClick={() => setEditTaxRatePct('0')}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Waive tax (0%)
            </button>
          </div>

          <div className="flex justify-end mb-4">
            <div className="w-64 text-sm">
              <div className="flex justify-between py-1"><span className="text-gray-600">Subtotal:</span><span className="font-semibold">${editPreview.subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between py-1"><span className="text-gray-600">Buyer's Premium ({(editPreview.buyersPremiumRate * 100).toFixed(0)}%):</span><span className="font-semibold">${editPreview.buyersPremium.toFixed(2)}</span></div>
              <div className="flex justify-between py-1 border-b border-gray-300"><span className="text-gray-600">Sales Tax ({(editRate * 100).toFixed(1)}%):</span><span className="font-semibold">${editPreview.salesTax.toFixed(2)}</span></div>
              <div className="flex justify-between py-2 border-b-2 border-gray-900"><span className="font-bold">New Total:</span><span className="font-bold text-green-600">${editPreview.total.toFixed(2)}</span></div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleSaveEdit}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button
              onClick={cancelEdit}
              disabled={saving}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Admin Actions */}
      <div className="bg-white rounded-lg shadow p-6 print:hidden">
        <h3 className="font-bold mb-4">Admin Actions</h3>
        <div className="flex gap-3 flex-wrap">
          {invoice.status === 'unpaid' && (
            <button
              onClick={() => handleStatusUpdate('paid')}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Mark as Paid
            </button>
          )}
          {!editing && invoice.status === 'unpaid' && !invoice.paidAt && (
            <button
              onClick={startEdit}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Edit Invoice
            </button>
          )}
          {invoice.pickStatus === 'picked' && (
            <button
              onClick={() => navigate(`/pickup/${invoice.invoiceNumber}`)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              📋 Customer Pickup
            </button>
          )}
          {(invoice.status === 'paid' || invoice.paidAt) && (
            <button
              onClick={() => handleStatusUpdate('unpaid')}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Mark as Unpaid
            </button>
          )}
        </div>
        {(invoice.status === 'paid' || invoice.paidAt) && (
          <p className="text-xs text-gray-500 mt-3">
            Paid invoices can't be edited. Mark it unpaid first to make changes.
          </p>
        )}
        {invoice.editedAt && (
          <p className="text-xs text-gray-400 mt-2">Last edited {new Date(invoice.editedAt).toLocaleString()}</p>
        )}
      </div>
    </div>
  );
};

export default InvoiceDetail;